"use client";

// status: updated

import { useState, useRef, useEffect } from "react";
import { Videos } from "@/app/assets/shared";
import { Images } from "@assets/assets";
import Image, { type StaticImageData } from "next/image";
import DeferredVideo from "@/components/DeferredVideo";
import { ensureGsap } from "@/lib/EnsureGsap";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { whenIdle } from "@/lib/whenIdle";

interface WhychooseCard {
  icon?: React.ReactNode | StaticImageData;
  heading: string;
  description: string;
}

interface WhychooseConstantProps {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  button: React.ReactNode;
  showLogos?: boolean;
  cards?: WhychooseCard[];
}

const DefaultCardIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 2L16.5 8.5L23 11L16.5 13.5L14 20L11.5 13.5L5 11L11.5 8.5L14 2Z"
      stroke="white"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M22 18L23 20.5L25.5 21.5L23 22.5L22 25L21 22.5L18.5 21.5L21 20.5L22 18Z"
      stroke="white"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const DEFAULT_CARDS: WhychooseCard[] = [
  {
    icon: Images?.whyChoose1,
    heading: "AI-Powered Innovation",
    description:
      "We integrate AI, intelligent capabilities, and automation into digital business solutions that allow modern organizations to deliver efficient and future-ready experiences.",
  },
  {
    icon: Images?.whyChoose2,
    heading: "Business-Centric Approach",
    description:
      "Every solution we develop is shaped around customer needs, business objectives, and future growth plans to help businesses drive measurable outcomes and improve efficiency.",
  },
  {
    icon: Images?.whyChoose3,
    heading: "Technical Excellence",
    description:
      "At Konstant, our team of tech-experts bring hands-on experience across modern programming languages, emerging digital capabilities, and modern frameworks.",
  },
  {
    icon: Images?.whyChoose4,
    heading: "Scalable Solutions",
    description:
      "From scalable technology foundations to flexible architectures, our solutions are designed to scale with increasing users and changing market demands.",
  },
  {
    icon: Images?.whyChoose5,
    heading: "Industry Knowledge",
    description:
      "We combine technical expertise with modern business requirements to build scalable digital solutions that address customer expectations and industry challenges.",
  },
  {
    icon: Images?.whyChoose6,
    heading: "Long-Term Support",
    description:
      "As a dependable technology partner, our partnership goes beyond initial project launch to help businesses achieve sustainable growth and continuous innovation.",
  },
];

export default function WhychooseConstant({
  title,
  subtitle,
  description,
  button,
  showLogos = true,
  cards = DEFAULT_CARDS,
}: WhychooseConstantProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const marqueeContentRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<any | null>(null);

  const [inView, setInView] = useState(false);

  const isMobile = useIsMobile();

  /*
   * Client logos
   */
  const logos = [
    { src: Images?.logoSet.bonusway, name: "Bonusway" },
    { src: Images?.logoSet.delMonte, name: "DelMonte" },
    { src: Images?.logoSet.festivity, name: "Festivity" },
    { src: Images?.logoSet.nasscom, name: "NASSCOM" },
    { src: Images?.logoSet.paceFunding, name: "PaceFunding" },
    { src: Images?.logoSet.rawbank, name: "Rawbank" },
    { src: Images?.logoSet.rayTech, name: "RayTech" },
    { src: Images?.logoSet.unitedNations, name: "United Nations" },
    { src: Images?.logoSet.volkswagen, name: "Volkswagen" },
  ];

  /*
   * Logos marquee
   *
   * Only initialize GSAP when showLogos is true.
   */
  useEffect(() => {
    if (!showLogos) {
      tlRef.current?.kill();
      tlRef.current = null;
      return;
    }

    const content = marqueeContentRef.current;

    if (!content) return;

    let cancelled = false;

    const initMarquee = async () => {
      const { gsap } = await ensureGsap();

      if (cancelled) return;

      requestAnimationFrame(() => {
        if (cancelled || !content) return;

        const contentWidth = content.scrollWidth / 2;

        if (!contentWidth) return;

        gsap.set(content, {
          x: 0,
        });

        // Kill any previous animation before creating a new one
        tlRef.current?.kill();

        tlRef.current = gsap.to(content, {
          x: -contentWidth,
          duration: 45,
          ease: "none",
          repeat: -1,
          modifiers: {
            x: gsap.utils.unitize((x) => parseFloat(x) % contentWidth),
          },
        });
      });
    };

    /*
     * Defer marquee initialization until browser is idle.
     * This avoids unnecessary layout work during initial load.
     */
    const cancelIdle = whenIdle(initMarquee);

    return () => {
      cancelled = true;

      cancelIdle();

      tlRef.current?.kill();
      tlRef.current = null;
    };
  }, [showLogos]);

  /*
   * Section scroll animation
   */
  useEffect(() => {
    const el = sectionRef.current;

    if (!el) return;

    let ctx: any;
    let cancelled = false;

    const cancelIdle = whenIdle(async () => {
      if (cancelled) return;

      const { gsap } = await ensureGsap();

      if (cancelled) return;

      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,

            // Start when the top of the section reaches
            // 90% of the viewport
            start: "top 90%",

            end: "bottom 20%",

            scrub: true,

            onEnter: () => setInView(true),

            // Uncomment if you want the video to reset
            // when scrolling back above the section.
            // onLeaveBack: () => setInView(false),
          },
        });

        tl.to(el.querySelector(".why-choose-main"), {
          y: 0,
          opacity: 1,
          ease: "power1.out",
          duration: 1,
        });
      }, el);
    });

    return () => {
      cancelled = true;

      cancelIdle();

      ctx?.revert?.();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="why-choose-constant relative z-[2] w-full overflow-hidden bg-[#000000]"
    >
      {/* =========================
          Texture Background
      ========================== */}
      <div className="texture-bg mix-blend-overlay absolute left-0 top-0 z-0 h-full w-full">
        {!isMobile && (
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        )}
      </div>

      {/* =========================
          Background Blue Glow - Top Right
      ========================== */}
      <div
        className="
          absolute
          right-0
          top-0
          h-[907.05px]
          w-[907.05px]
          -translate-y-1/3
          translate-x-1/3
          rounded-full
          bg-[#225bd4]/40
          blur-[333.68px]
        "
      />

      {/* =========================
          Background Blue Glow - Bottom Left
      ========================== */}
      <div
        className="
          absolute
          bottom-0
          left-0
          h-[907.05px]
          w-[907.05px]
          -translate-x-1/3
          translate-y-1/3
          rounded-full
          bg-[#225bd4]/40
          blur-[333.68px]
        "
      />

      {/* =========================
          Main Content
      ========================== */}
      <div
        className={`
    why-choose-main
    relative
    z-10
    mx-auto
    w-[calc(100%_-_60px)]
    max-w-[5000px]
    pt-[20px]
    lg:pt-[80px]
    ${!showLogos ? "pb-[100px]" : ""}
  `}
      >
        <div className="why-choose-main-content flex gap-0">
          <div className="w-full px-0 pb-0 pt-6 lg:px-6 lg:pb-10">
            {/* Subtitle */}
            <p className="mb-0 w-full justify-center text-base font-medium uppercase tracking-tight text-white lg:mb-[30px]">
              {subtitle}
            </p>

            <div className="flex w-full flex-col gap-[20px] pb-4">
              {/* =========================
                  Top Content Row
              ========================== */}
              <div className="flex w-full flex-col items-start justify-between gap-8 2xl:flex-row">
                {/* Video Blob */}
                {inView &&
                  typeof window !== "undefined" &&
                  window.innerWidth > 1560 && (
                    <div className="flex min-w-[400px] flex-1 items-center justify-center">
                      <DeferredVideo
                        src={Videos.blob}
                        className="h-auto w-[350px] rounded-xl object-cover"
                        preload="none"
                        ariaLabel="blob video"
                      />
                    </div>
                  )}

                {/* Title + Description */}
                <div>
                  <h2 className="clash-font max-w-[18ch] text-[length:var(--section-title)] font-medium leading-[110%] text-white">
                    {title}
                  </h2>

                  <p className="max-w-[80ch] pt-[20px] text-[length:var(--hero-para)] font-normal leading-[160%] text-white/50 lg:pt-0">
                    {description}
                  </p>
                </div>

                {/* Button */}
                <div className="2xl:self-start">{button}</div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================
            Feature Cards Grid
        ========================== */}
        <div className="w-full">
          <div className="mt-2 grid w-full grid-cols-1 overflow-hidden p-[1.5px] sm:mt-8 sm:grid-cols-2 lg:mt-1 lg:grid-cols-3">
            {cards.map((card, index) => (
              <div
                key={index}
                className="-ml-[1.5px] -mt-[1.5px] flex flex-col items-start gap-6 border-[1.5px] border-[#ECF0F6]/10 bg-white/[0.08] p-5 text-left sm:gap-7 sm:p-6 lg:gap-9 lg:p-8"
              >
                {/* Icon */}
                <div className="[&_svg]:h-8 [&_svg]:w-8 sm:[&_svg]:h-9 sm:[&_svg]:w-9 lg:[&_svg]:h-12 lg:[&_svg]:w-12">
                  {card.icon ? (
                    typeof card.icon === "object" && "src" in card.icon ? (
                      <Image
                        src={card.icon}
                        alt=""
                        width={48}
                        height={48}
                        className="h-8 w-8 object-contain sm:h-9 sm:w-9 lg:h-14 lg:w-14"
                      />
                    ) : typeof card.icon === "string" ? (
                      <Image
                        src={card.icon}
                        alt=""
                        width={48}
                        height={48}
                        className="h-8 w-8 object-contain sm:h-9 sm:w-9 lg:h-14 lg:w-14"
                      />
                    ) : (
                      card.icon
                    )
                  ) : (
                    <DefaultCardIcon />
                  )}
                </div>

                {/* Content */}
                <div className="parent-wrapper w-full">
                  <h3 className="clash-font text-xl font-medium leading-[1.2] text-white sm:text-2xl lg:text-[1.65rem]">
                    {card.heading}
                  </h3>

                  <p className="mt-2 text-sm font-normal leading-[160%] text-[#949596] sm:text-base lg:text-lg">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* =========================
          Trusted By / Logos
          Default: visible
          Hide with showLogos={false}
      ========================== */}
      {showLogos && (
        <>
          {/* Trusted By Title */}
          <p className="pt-10 text-center text-[length:var(--hero-para)] font-normal uppercase tracking-wide text-white lg:py-10 lg:pt-15">
            Trusted By
          </p>

          {/* Logos Marquee */}
          <div className="client-logos w-full overflow-hidden py-[20px] xl:pb-[70px]">
            <div
              ref={marqueeContentRef}
              className="
                flex
                max-w-[max-content]
                gap-[50px]
                whitespace-nowrap
                will-change-transform
              "
            >
              {[...logos, ...logos].map((logo, i) => (
                <div
                  key={i}
                  className="group relative w-[85px] flex-shrink-0 lg:w-[10vw]"
                >
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={200}
                    height={140}
                    className="mx-auto max-h-[140px] object-contain"
                    sizes="200px"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
